import React, { useState, useEffect } from "react";
import { api, BusinessUnit } from "../api";

const AdminBusinessUnits: React.FC = () => {
  const [units, setUnits] = useState<BusinessUnit[]>([]);
  const [loading, setLoading] = useState(true);

  // Add form
  const [showAdd, setShowAdd] = useState(false);
  const [addCode, setAddCode] = useState("");
  const [addName, setAddName] = useState("");
  const [addError, setAddError] = useState("");

  // Edit
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editCode, setEditCode] = useState("");
  const [editName, setEditName] = useState("");

  useEffect(() => { loadBUs(); }, []);

  const loadBUs = () => {
    setLoading(true);
    api.get("/business-units")
      .then(res => { setUnits(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const handleAdd = async () => {
    if (!addCode.trim() || !addName.trim()) { setAddError("Code and name required"); return; }
    try {
      await api.post("/business-units", { code: addCode.trim(), name: addName.trim() });
      setShowAdd(false);
      setAddCode("");
      setAddName("");
      setAddError("");
      loadBUs();
    } catch (err: any) {
      setAddError(err.response?.data?.error || "Failed to create");
    }
  };

  const handleSaveEdit = async (id: number) => {
    try {
      await api.put(`/business-units/${id}`, { code: editCode.trim(), name: editName.trim() });
      setEditingId(null);
      loadBUs();
    } catch (err: any) {
      alert(err.response?.data?.error || "Failed to update");
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Delete "${name}"? This will fail if cost centers are still linked.`)) return;
    try {
      await api.delete(`/business-units/${id}`);
      loadBUs();
    } catch (err: any) {
      alert(err.response?.data?.error || "Failed to delete");
    }
  };

  if (loading) return <div style={{ padding: 20, color: "#9ca3af" }}>Loading business units...</div>;

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <span className="material-icons">business</span>
        <span className="admin-section-title">Business Units</span>
        <span className="admin-section-subtitle">{units.length} Business Unit{units.length !== 1 ? "s" : ""}</span>
      </div>
      <div className="admin-section-body">
        {/* Toolbar */}
        <div className="admin-cc-toolbar">
          <button className="admin-btn admin-btn--primary" onClick={() => setShowAdd(!showAdd)}>
            <span className="material-icons">{showAdd ? "close" : "add"}</span>
            {showAdd ? "Cancel" : "Add Business Unit"}
          </button>
        </div>

        {/* Add Form */}
        {showAdd && (
          <div className="admin-add-form">
            <input
              className="admin-text-input"
              placeholder="Code (e.g. CORP)"
              value={addCode}
              onChange={e => setAddCode(e.target.value)}
              style={{ maxWidth: 160 }}
            />
            <input
              className="admin-text-input"
              placeholder="Name (e.g. Central - CORP)"
              value={addName}
              onChange={e => setAddName(e.target.value)}
              style={{ flex: 1 }}
            />
            <button className="admin-btn admin-btn--success" onClick={handleAdd}>
              <span className="material-icons">check</span> Create
            </button>
            {addError && <span className="admin-toast admin-toast--err">{addError}</span>}
          </div>
        )}

        {/* Table */}
        <table className="admin-cc-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th style={{ textAlign: "center" }}>Cost Centers</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {units.map(bu => (
              <tr key={bu.id}>
                {editingId === bu.id ? (
                  <>
                    <td>
                      <input
                        className="admin-text-input"
                        value={editCode}
                        onChange={e => setEditCode(e.target.value)}
                        style={{ maxWidth: 140 }}
                      />
                    </td>
                    <td>
                      <input
                        className="admin-text-input"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                      />
                    </td>
                    <td style={{ textAlign: "center" }}>{bu.cc_count || 0}</td>
                    <td style={{ textAlign: "right" }}>
                      <div className="admin-btn-row" style={{ justifyContent: "flex-end" }}>
                        <button className="admin-btn admin-btn--success" onClick={() => handleSaveEdit(bu.id)}>Save</button>
                        <button className="admin-btn admin-btn--ghost" onClick={() => setEditingId(null)}>Cancel</button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td><span className="admin-cc-num">{bu.code}</span></td>
                    <td>{bu.name}</td>
                    <td style={{ textAlign: "center", color: "#64748b" }}>{bu.cc_count || 0}</td>
                    <td style={{ textAlign: "right" }}>
                      <div className="admin-btn-row" style={{ justifyContent: "flex-end" }}>
                        <button className="admin-btn admin-btn--ghost" onClick={() => { setEditingId(bu.id); setEditCode(bu.code); setEditName(bu.name); }}>
                          <span className="material-icons">edit</span> Edit
                        </button>
                        <button className="admin-btn admin-btn--danger" onClick={() => handleDelete(bu.id, bu.name)}>
                          <span className="material-icons">delete</span>
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {units.length === 0 && (
              <tr><td colSpan={4} style={{ textAlign: "center", padding: 20, color: "#9ca3af" }}>No business units defined yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminBusinessUnits;
